import type { Theme } from "@mui/material";
import type { IconMapping } from "@foodpilot/foods";
import { Topic, SocialPostType } from "../types/post.types";

type FoodsIconName = keyof IconMapping;

type PostTypeColorToken = "blue" | "green" | "coral" | "grey";

export type SocialPostTypeVisual = {
    icon: FoodsIconName;
    colorToken: PostTypeColorToken;
    labelKey: string;
    defaultLabel: string;
};

type SocialPostTypeConfig = {
    hashtag: string;
    labels: string[];
};

export const SOCIAL_POST_TYPE_VISUAL: Record<SocialPostType, SocialPostTypeVisual> = {
    generic: {
        icon: "forum",
        colorToken: "grey",
        labelKey: "social.post.type.generic",
        defaultLabel: "Post",
    },
    question: {
        icon: "questionMark",
        colorToken: "blue",
        labelKey: "social.post.type.question",
        defaultLabel: "Question",
    },
    best_practice: {
        icon: "suggestions",
        colorToken: "green",
        labelKey: "social.post.type.best_practice",
        defaultLabel: "Best practice",
    },
    experience: {
        icon: "cheer",
        colorToken: "coral",
        labelKey: "social.post.type.experience",
        defaultLabel: "Experience",
    },
};

const POST_TYPE_COLOR_FALLBACK: Record<PostTypeColorToken, string> = {
    blue: "#22B8DA",
    green: "#17BF3C",
    coral: "#FF816C",
    grey: "#A3A2A0",
};

export const getPostTypeColor = (theme: Theme, colorToken: PostTypeColorToken): string => {
    const custom = theme.custom as Theme["custom"] & {
        blue?: Record<number, string>;
        coral?: Record<number, string>;
    };

    switch (colorToken) {
        case "blue":
            return custom.blue?.[500] ?? POST_TYPE_COLOR_FALLBACK.blue;
        case "green":
            return custom.green[500] ?? POST_TYPE_COLOR_FALLBACK.green;
        case "coral":
            return custom.coral?.[500] ?? POST_TYPE_COLOR_FALLBACK.coral;
        case "grey":
        default:
            return custom.grey[1400] ?? POST_TYPE_COLOR_FALLBACK.grey;
    }
};

export const SOCIAL_POST_TYPE_CONFIG: Record<Exclude<SocialPostType, "generic">, SocialPostTypeConfig> = {
    question: {
        hashtag: "question",
        labels: ["question"],
    },
    best_practice: {
        hashtag: "bonne-pratique",
        labels: ["bonne-pratique", "best-practice", "best practice"],
    },
    experience: {
        hashtag: "retour-experience",
        labels: ["retour-experience", "experience", "feedback"],
    },
};

const normalizeToken = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/_/g, "-");

const extractHashtags = (content: string) =>
    Array.from(content.matchAll(/#([\p{L}\p{N}_-]+)/gu)).map((match) => normalizeToken(match[1]));

export const inferPostType = (content: string, topics: Topic[] = []): SocialPostType => {
    const tokens = new Set([...extractHashtags(content), ...topics.map((topic) => normalizeToken(topic.name))]);

    const typedEntry = Object.entries(SOCIAL_POST_TYPE_CONFIG).find(([, config]) =>
        config.labels.some((label) => tokens.has(normalizeToken(label))),
    );

    return (typedEntry?.[0] as SocialPostType | undefined) ?? "generic";
};

export const injectTypeHashtag = (content: string, type: SocialPostType): string => {
    if (type === "generic" || inferPostType(content) === type) {
        return content.trim();
    }

    return `#${SOCIAL_POST_TYPE_CONFIG[type].hashtag} ${content.trim()}`.trim();
};

export const stripTypeHashtagFromContent = (content: string, postType: SocialPostType): string => {
    if (postType === "generic") {
        return content;
    }

    const hashtag = SOCIAL_POST_TYPE_CONFIG[postType].hashtag;
    const pattern = new RegExp(`^\\s*#${hashtag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*`, "iu");

    return content.replace(pattern, "").trim();
};

/** Hashtags auto-injected by post types — not free community topics. */
export const isPostTypeHashtag = (name: string): boolean => {
    const normalized = normalizeToken(name);

    return Object.values(SOCIAL_POST_TYPE_CONFIG).some((config) =>
        [config.hashtag, ...config.labels].some((label) => normalizeToken(label) === normalized),
    );
};

/** Topics shown as #hashtags — excludes post-type system hashtags (shown via PostTypeBadge). */
export const filterUserTopics = <T extends { name: string }>(topics: T[]): T[] =>
    topics.filter((topic) => !isPostTypeHashtag(topic.name));

/** Publication categories shown in the sidebar (excludes generic). */
export const PUBLICATION_CATEGORIES = ["question", "best_practice", "experience"] as const satisfies readonly Exclude<
    SocialPostType,
    "generic"
>[];

export type PublicationCategory = (typeof PUBLICATION_CATEGORIES)[number];

export const matchesPublicationCategory = (content: string, topics: Topic[], category: PublicationCategory): boolean =>
    inferPostType(content, topics) === category;
